import { Event } from "@/lib/types";
import { CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface EventDetailsContentProps {
  event: Event;
}

export const EventDetailsContent = ({ event }: EventDetailsContentProps) => {
  const navigate = useNavigate();

  // Parse the date without timezone conversion
  const rawDate = new Date(event.date);
  const formattedDate = `${rawDate.toLocaleString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC' // Use UTC to prevent local timezone conversion
  })}`;

  return (
    <div className="bg-white rounded-b-lg p-8 space-y-8">
      <div className="prose prose-lg max-w-none text-black">
        <h2 className="text-3xl font-semibold mb-6 text-[#9b87f5]">About This Event</h2>
        <p className="text-xl mb-6 whitespace-pre-wrap">{event.description}</p>

        {event.featuredArtists && event.featuredArtists.length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-semibold mb-4">Featuring:</h3>
            <ul className="space-y-4 pl-4">
              {event.featuredArtists.map((artist: { name: string, description: string }, index) => (
                <li key={index} className="flex items-start space-x-3 text-xl">
                  <span className="text-yellow-400">⭐</span>
                  <span>
                    <strong>{artist.name}</strong> – {artist.description}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-4 text-black">
              <CalendarDays className="h-6 w-6 flex-shrink-0" />
              <div>
                <p className="font-semibold text-xl">Date</p>
                <p className="text-lg">{formattedDate}</p>
                {event.dateExtraNote && (
                  <p className="text-sm text-gray-600 italic mt-1">{event.dateExtraNote}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4 text-black">
              <Clock className="h-6 w-6 flex-shrink-0" />
              <div>
                <p className="font-semibold text-xl">Time</p>
                <p className="text-lg">{event.eventTimeRange}</p>
                {event.timeExtraNote && (
                  <p className="text-sm text-gray-600 italic mt-1">{event.timeExtraNote}</p>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-4 text-black">
              <MapPin className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-xl">Location</p>
                {event.locationUrl ? (
                  <a
                    href={event.locationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-purple-600 hover:text-purple-800"
                  >
                    {event.location}
                  </a>
                ) : (
                  <p className="text-lg">{event.location}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {event.producerName && (
            <div className="space-y-2 text-black">
              <p className="font-semibold text-xl flex items-center gap-2">
                <span>👥</span> Producer
              </p>
              <div className="ml-8">
                {event.producerUrl ? (
                  <a
                    href={event.producerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-purple-600 hover:text-purple-800"
                  >
                    {event.producerName}
                  </a>
                ) : (
                  <p className="text-lg">{event.producerName}</p>
                )}
              </div>
            </div>
          )}

          {event.refundPolicy && (
            <div className="space-y-2 text-black">
              <p className="font-semibold text-xl flex items-center gap-2">
                <span>💰</span> Refund Policy
              </p>
              <div className="ml-8">
                <p className="text-lg">{event.refundPolicy}</p>
              </div>
            </div>
          )}

          {event.predictedFansRange && event.predictedFansRange !== "0" && (
            <div className="flex items-center space-x-4 text-black">
              <Users className="h-6 w-6" />
              <div>
                <p className="font-semibold text-xl">Predicted Fans Attending</p>
                <p className="text-lg">{event.predictedFansRange}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-1">
            <p className="text-xl text-black">Standing Admission</p>
            <div className="flex items-baseline space-x-2">
              <p className="text-3xl font-bold text-black">
                ${event.priceGeneralAdmission}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/events/${event.seoUrl}/ticketing`)}
            className="w-full md:w-auto bg-purple-600 hover:bg-green-500 transition-colors duration-300 text-xl py-4 px-8 text-white"
          >
            Get Tickets
          </Button>
        </div>
      </div>
    </div>
  );
};
